import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { requiredAge } from "./format";

const Ctx = createContext(null);

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem("cart") || "[]"); } catch { return []; }
};

export const lineTotal = (i) =>
  (i.unit_price + i.extras.reduce((s, e) => s + e.unit_price * e.quantity, 0)) * i.quantity;

export function CartProvider({ children }) {
  // Lazy init from localStorage so a hard reload / direct /cart or /checkout URL keeps the cart.
  const [items, setItems] = useState(loadCart);
  const [generalNote, setGeneralNote] = useState("");
  const [orderType, setOrderType] = useState("pickup");

  useEffect(() => {
    try { localStorage.setItem("cart", JSON.stringify(items)); } catch {}
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => [...prev, { ...item, line_id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }]);
  }, []);
  const updateItem = useCallback((lineId, patch) => {
    setItems((prev) => prev.map((i) => (i.line_id === lineId ? { ...i, ...patch } : i)));
  }, []);
  const removeItem = useCallback((lineId) => setItems((prev) => prev.filter((i) => i.line_id !== lineId)), []);
  const clear = useCallback(() => { setItems([]); setGeneralNote(""); }, []);

  // Track ids of orders this browser placed (parity with Mobile addMyOrder; supports guest lookups).
  const addMyOrder = useCallback((id) => {
    try {
      const prev = JSON.parse(localStorage.getItem("my_orders") || "[]");
      localStorage.setItem("my_orders", JSON.stringify([id, ...prev.filter((x) => x !== id)].slice(0, 30)));
    } catch {}
  }, []);

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    const extrasTotal = items.reduce((s, i) => s + i.extras.reduce((a, e) => a + e.unit_price * e.quantity, 0) * i.quantity, 0);
    return {
      items,
      generalNote,
      setGeneralNote,
      addItem,
      updateItem,
      removeItem,
      clear,
      addMyOrder,
      count: items.reduce((s, i) => s + i.quantity, 0),
      subtotal,
      extrasTotal,
      hasAlcohol: items.some((i) => i.is_alcohol),
      requiredAge: requiredAge(items),
      orderType,
      setOrderType,
    };
  }, [items, generalNote, addItem, updateItem, removeItem, clear, addMyOrder, orderType]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  const c = useContext(Ctx);
  if (!c) throw new Error("CartProvider missing");
  return c;
}
